"use client"

import type {
  NotificationType,
  NotificationSoundPreference,
} from '@/types/notification'

// ── Browser Notification Permission ──────────────────────────────────────────

export type BrowserPermissionState = 'granted' | 'denied' | 'default' | 'unsupported'

export function getBrowserPermissionState(): BrowserPermissionState {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported'
  }
  return Notification.permission as BrowserPermissionState
}

export async function requestBrowserPermission(): Promise<BrowserPermissionState> {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported'
  }
  try {
    const result = await Notification.requestPermission()
    return result as BrowserPermissionState
  } catch {
    // A throw is not a denial. Safari rejects `requestPermission()` when it is
    // called outside a user gesture — which the auto-request on a finished
    // timer is — and reporting that as 'denied' told the user their browser had
    // blocked notifications when it had not been asked yet.
    return getBrowserPermissionState()
  }
}

/**
 * Watches the browser's notification permission for out-of-app changes.
 *
 * The permission lives in browser settings, so the user can change it while the
 * tab sits open — going to Safari → Settings → Websites → Notifications and
 * coming back is the normal case. The Permissions API reports that directly
 * where it exists; focus and visibility changes cover the browsers where it
 * does not (Safari only added `permissions.query` for notifications recently).
 */
export function subscribeToBrowserPermission(
  onChange: (state: BrowserPermissionState) => void,
): () => void {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return () => {}
  }

  const report = () => onChange(getBrowserPermissionState())
  const onVisible = () => {
    if (document.visibilityState === 'visible') report()
  }

  window.addEventListener('focus', report)
  document.addEventListener('visibilitychange', onVisible)

  let status: PermissionStatus | null = null
  void navigator.permissions
    ?.query({ name: 'notifications' as PermissionName })
    .then((result) => {
      status = result
      result.addEventListener('change', report)
    })
    .catch(() => {
      // Not supported here — the focus/visibility listeners still cover it.
    })

  return () => {
    window.removeEventListener('focus', report)
    document.removeEventListener('visibilitychange', onVisible)
    status?.removeEventListener('change', report)
  }
}

// ── Service Worker registration ──────────────────────────────────────────────
// A minimal SW that only handles notificationclick events (no fetch interception).
// Lets us use registration.showNotification() which works reliably even when
// the tab is in the background or minimised.

let _swRegistration: ServiceWorkerRegistration | null = null

async function ensureServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (_swRegistration) return _swRegistration
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null
  try {
    _swRegistration = await navigator.serviceWorker.register('/notification-sw.js', {
      scope: '/',
    })
    // Wait for the SW to be ready
    _swRegistration = await navigator.serviceWorker.ready
    return _swRegistration
  } catch (err) {
    console.warn('Could not register notification service worker:', err)
    return null
  }
}

// Start SW registration as soon as the module loads (non-blocking)
if (typeof window !== 'undefined') {
  ensureServiceWorker()
}

// ── Icon mapping ─────────────────────────────────────────────────────────────

function getNotificationIcon(type: NotificationType): string {
  switch (type) {
    case 'timer_complete':
      return '/icons/timer-complete.png'
    case 'pomodoro_focus_end':
      return '/icons/pomodoro-focus.png'
    case 'pomodoro_break_end':
      return '/icons/pomodoro-break.png'
    case 'task_upcoming':
    case 'task_overdue':
      return '/icons/task-reminder.png'
  }
}

// ── Sound playback (Web Audio API) ───────────────────────────────────────────

const SOUND_URLS: Record<Exclude<NotificationSoundPreference, 'mute'>, string> = {
  standard: '/sounds/standard.mp3',
  sky:      '/sounds/sky.mp3',
  dash:     '/sounds/dash.mp3',
}

let _audioContext: AudioContext | null = null
// Per-sound buffer and in-flight load promise caches
const _buffers: Partial<Record<string, AudioBuffer>> = {}
const _loadPromises: Partial<Record<string, Promise<void>>> = {}

/** Lazily create (or return) the singleton AudioContext. */
function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null
  if (!_audioContext) {
    const AudioCtx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    if (!AudioCtx) return null
    _audioContext = new AudioCtx()
  }
  return _audioContext
}

/** Pre-fetch and decode a sound file into an AudioBuffer, keyed by URL. */
function ensureBufferLoaded(url: string): Promise<void> {
  if (_buffers[url]) return Promise.resolve()
  if (_loadPromises[url]) return _loadPromises[url]!

  const ctx = getAudioContext()
  if (!ctx) return Promise.resolve()

  _loadPromises[url] = fetch(url)
    .then((r) => r.arrayBuffer())
    .then((buf) => ctx.decodeAudioData(buf))
    .then((decoded) => {
      _buffers[url] = decoded
    })
    .catch((err) => {
      console.warn(`Failed to pre-load notification sound (${url}):`, err)
    })
    .finally(() => {
      delete _loadPromises[url]
    })

  return _loadPromises[url]!
}

/**
 * Resume the AudioContext and pre-load all notification sounds.
 * Called on every user interaction (click / touch / keydown) to keep the
 * context alive. Safe and cheap to call repeatedly.
 */
export function unlockAudio(): void {
  const ctx = getAudioContext()
  if (ctx?.state === 'suspended') {
    ctx.resume().catch(() => { /* ignore */ })
  }
  // Pre-load all sounds so they're ready when needed
  for (const url of Object.values(SOUND_URLS)) {
    ensureBufferLoaded(url)
  }
}

// Keep the AudioContext alive by re-unlocking on every user interaction.
// This is intentionally NOT one-shot — the browser may suspend the context
// after a period of inactivity or when the tab goes to the background.
if (typeof document !== 'undefined') {
  const onInteraction = () => unlockAudio()
  document.addEventListener('click', onInteraction, { capture: true })
  document.addEventListener('touchstart', onInteraction, { capture: true })
  document.addEventListener('keydown', onInteraction, { capture: true })
}

/**
 * Internal async sound playback for a given URL.
 * Properly awaits ctx.resume() before attempting playback.
 */
async function playSoundAsync(url: string): Promise<void> {
  const ctx = getAudioContext()

  // Primary path: Web Audio API — works from timers/intervals once unlocked
  if (ctx) {
    // Await the resume so the context is actually running before we play
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume()
      } catch { /* ignore — will fall through to HTML5 Audio */ }
    }

    await ensureBufferLoaded(url)

    const buffer = _buffers[url]
    if (buffer && ctx.state === 'running') {
      try {
        const source = ctx.createBufferSource()
        source.buffer = buffer
        const gain = ctx.createGain()
        gain.gain.value = 0.6
        source.connect(gain)
        gain.connect(ctx.destination)
        source.start(0)
        return
      } catch (err) {
        console.warn('Web Audio playback failed, trying fallback:', err)
      }
    }
  }

  // Fallback: HTML5 Audio (may be blocked from non-gesture contexts)
  try {
    const audio = new Audio(url)
    audio.volume = 0.6
    await audio.play()
  } catch {
    console.warn('Audio fallback also failed — browser likely blocking non-gesture playback')
  }
}

/** Fire-and-forget wrapper so callers don't need to await. */
function playSound(url: string): void {
  playSoundAsync(url).catch(() => { /* already logged internally */ })
}

/**
 * Play a notification sound based on the user's preference.
 * - 'standard': Plays standard.mp3
 * - 'sky':      Plays sky.mp3
 * - 'done':     Plays done.mp3
 * - 'mute':     No sound
 */
export function playNotificationSound(preference: NotificationSoundPreference): void {
  if (preference === 'mute') return
  const url = SOUND_URLS[preference]
  if (url) playSound(url)
}

// ── Send browser notification ────────────────────────────────────────────────

interface SendNotificationOptions {
  type: NotificationType
  title: string
  body: string
  soundPreference?: NotificationSoundPreference
  tag?: string
  onClick?: () => void
}

/**
 * Send a browser / OS notification with sound.
 *
 * Uses the Service Worker's `showNotification()` for reliable delivery even
 * when the tab is in the background. Falls back to `new Notification()` if
 * the SW is not yet registered.
 */
export function sendBrowserNotification(options: SendNotificationOptions): void {
  const {
    type,
    title,
    body,
    soundPreference = 'standard',
    tag,
    onClick,
  } = options

  // Play our custom sound
  if (soundPreference !== 'mute') {
    playNotificationSound(soundPreference)
  }

  const permission = getBrowserPermissionState()
  if (permission !== 'granted') return

  const useSilent = true
  const notifTag = tag ?? `blitztask-${type}-${Date.now()}`
  const icon = getNotificationIcon(type)
  const requireInteraction = type === 'timer_complete' || type === 'task_overdue'

  // Try Service Worker first (reliable in background tabs)
  const reg = _swRegistration
  if (reg) {
    reg
      .showNotification(title, {
        body,
        icon,
        tag: notifTag,
        requireInteraction,
        silent: useSilent,
      })
      .catch(() => {
        // SW notification failed — fall back to Notification constructor
        showFallbackNotification(title, body, icon, notifTag, requireInteraction, useSilent, onClick)
      })
    return
  }

  // Fallback: Notification constructor (works when tab is focused)
  showFallbackNotification(title, body, icon, notifTag, requireInteraction, useSilent, onClick)
}

function showFallbackNotification(
  title: string,
  body: string,
  icon: string,
  tag: string,
  requireInteraction: boolean,
  silent: boolean,
  onClick?: () => void,
): void {
  try {
    const notification = new Notification(title, {
      body,
      icon,
      tag,
      requireInteraction,
      silent,
    })

    if (onClick) {
      notification.onclick = () => {
        window.focus()
        onClick()
        notification.close()
      }
    }

    if (!requireInteraction) {
      setTimeout(() => notification.close(), 10_000)
    }
  } catch {
    // Notification constructor not available
  }
}
